import React from 'react';
import { Tag } from '@decisiv/ui-components';

/**
 * Renders a table cell value given a column definition and a record.
 *
 * Supported types: 'link' | 'boolean' | 'date' | default (plain text).
 * Any column can also supply a `render(record)` function as an escape hatch.
 */
// Long unbreakable values (OAuth UIDs, UUIDs, tokens) have no whitespace to
// wrap on, so they force their column wider than the page and the KDS table —
// which clips overflow rather than scrolling it — drops the right-hand columns
// off-screen. Allowing such content to break lets the auto table layout shrink
// the column so the whole table fits. minWidth:0 lets the cell's flex wrapper
// shrink below the content's intrinsic width.
const wrapStyle = {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  minWidth: 0,
};

export const renderCell = (col, record, resourceEndpoint) => {
  if (col.render) return col.render(record);

  const value = record[col.name];

  switch (col.type) {
    case 'link':
      // `linkText` lets a dedicated action column (one with no matching record
      // field, e.g. a "Details" column) supply its own anchor label; value
      // columns keep rendering their value.
      return (
        <a
          href={`${resourceEndpoint}/${record.id}`}
          style={{ textDecoration: 'underline', ...wrapStyle }}
        >
          {value ?? col.linkText ?? '—'}
        </a>
      );
    case 'boolean':
      return value == null
        ? <span>—</span>
        : <Tag intent={value ? 'success' : 'neutral'} text={value ? 'Yes' : 'No'} />;
    case 'date':
      return value ? new Date(value).toLocaleDateString() : '—';
    default:
      return <span style={wrapStyle}>{value ?? '—'}</span>;
  }
};

/**
 * Renders an attribute value on the show page.
 *
 * Mirrors renderCell but without the link type (show pages don't self-link).
 */
export const renderAttribute = (attrDef, record) => {
  if (attrDef.render) return attrDef.render(record);

  const value = record[attrDef.name];

  switch (attrDef.type) {
    case 'boolean':
      return value == null
        ? <span>—</span>
        : <Tag intent={value ? 'success' : 'neutral'} text={value ? 'Yes' : 'No'} />;
    case 'date':
      return <span>{value ? new Date(value).toLocaleDateString() : '—'}</span>;
    case 'json': {
      if (!value) return <span>—</span>;
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        return (
          <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f4f5f7', padding: 8, borderRadius: 4 }}>
            {JSON.stringify(parsed, null, 2)}
          </pre>
        );
      } catch (_) {
        return <span>{value}</span>;
      }
    }
    default:
      return <span>{value ?? '—'}</span>;
  }
};
